import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";

export interface UseRemoveWithConfirmOptions<TId = number> {
  /** 제거 시 호출할 API (id로 PATCH 등). 반환값은 사용하지 않음 */
  mutationFn: (id: TId) => Promise<unknown>;
  /** 성공 시 무효화할 쿼리 키 목록 */
  invalidateKeys: (id: TId) => (unknown[])[];
  /** 모달 제목 */
  title: string;
  /** 모달 본문 */
  message: string;
  /** 제거 버튼 라벨 (기본: "제거") */
  removeLabel?: string;
  /** API 실패 시 콜백 */
  onError?: (err: unknown) => void;
}

export interface RemoveWithConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "warning";
  title: string;
  message: string;
  buttons: Array<{
    label: string;
    onClick: () => void;
    variant: "secondary" | "danger";
  }>;
}

/**
 * "제거" 클릭 → 확인 모달 → 확인 시 즉시 API (null 반영) → 캐시 무효화 패턴을 위한 훅.
 * 상품 썸네일/이미지, 강사 프로필 이미지, 레슨 자료 등에서 공통 사용.
 *
 * @example
 * const removeThumbnail = useRemoveWithConfirm({
 *   mutationFn: (id) => updateProduct(id, { thumbnailUrl: null }),
 *   invalidateKeys: (id) => [["product", id]],
 *   title: "썸네일 제거",
 *   message: "썸네일 이미지를 제거하시겠습니까?",
 * });
 * // 버튼: onClick={removeThumbnail.openConfirm} disabled={removeThumbnail.isPending}
 * // 모달: <ConfirmModal {...removeThumbnail.getConfirmModalProps(id)} />
 */
export function useRemoveWithConfirm<TId = number>(
  options: UseRemoveWithConfirmOptions<TId>
): {
  isPending: boolean;
  isOpen: boolean;
  openConfirm: () => void;
  closeConfirm: () => void;
  getConfirmModalProps: (id: TId) => RemoveWithConfirmModalProps;
} {
  const {
    mutationFn,
    invalidateKeys,
    title,
    message,
    removeLabel = "제거",
    onError,
  } = options;

  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const mutation = useMutation({
    mutationFn,
    onSuccess: (_, id) => {
      invalidateKeys(id).forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
      setIsOpen(false);
    },
    onError: (err) => {
      onError?.(err);
    },
  });

  const openConfirm = useCallback(() => setIsOpen(true), []);
  const closeConfirm = useCallback(() => setIsOpen(false), []);

  const getConfirmModalProps = useCallback(
    (id: TId): RemoveWithConfirmModalProps => ({
      isOpen,
      onClose: closeConfirm,
      type: "warning",
      title,
      message,
      buttons: [
        {
          label: "취소",
          onClick: closeConfirm,
          variant: "secondary",
        },
        {
          label: removeLabel,
          onClick: () => {
            mutation.mutate(id);
            closeConfirm();
          },
          variant: "danger",
        },
      ],
    }),
    [isOpen, closeConfirm, title, message, removeLabel, mutation]
  );

  return {
    isPending: mutation.isPending,
    isOpen,
    openConfirm,
    closeConfirm,
    getConfirmModalProps,
  };
}
